import * as React from 'react';
import { useState } from 'react';
import { cn } from '../../lib/utils';
import { Button } from '../button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../dialog';
import { ViewEditToggle } from './ViewEditToggle';
import {
  FileViewerPane,
  hasEditToggle,
  type FileContent,
  type FileEditingResolveAction,
  type FileEditingState,
} from './FileViewerPane';

/**
 * Editing wiring for the whole tab strip. The
 * host owns every piece of state here; this component renders dirty dots,
 * the close-confirmation dialog, and threads the active tab's state into
 * {@link FileViewerPane}.
 */
export interface FileTabsEditing {
  /** Per-open-path editing state, keyed by path. */
  states: Record<string, FileEditingState>;
  /** True renders editors read-only with `readOnlyNotice`. */
  readOnly: boolean;
  readOnlyNotice?: string;
  onEdit: (path: string) => void;
  onBufferSync: (path: string, text: string) => void;
  onSave: (path: string) => void;
  /** `mergedText` accompanies ONLY the `merged` action (merge-view output). */
  onResolve: (path: string, action: FileEditingResolveAction, mergedText?: string) => void;
}

export interface FileTabsViewerProps {
  /** Paths of every open file, in tab order. */
  openPaths: string[];
  /** The path whose content is shown, or null when nothing is open. */
  activePath: string | null;
  /** Activate an already-open tab. */
  onActivate: (path: string) => void;
  /** Close a tab. The host decides which neighbor becomes active. */
  onClose: (path: string) => void;
  /** Reads a file's content + encoding (host-provided real backend read). */
  read: (path: string) => Promise<FileContent>;
  /** Byte size of the active file (from the tree listing) — lets the viewer
   *  refuse to fetch an oversized file before reading it. */
  activeSizeBytes?: number;
  /** Open-in-OS (desktop) / download (web) for unsupported formats. May be
   *  async (desktop IPC round-trip); the pane surfaces rejections. */
  onOpenExternally?: (path: string, bytes: Uint8Array) => void | Promise<void>;
  /** Controlled-editing wiring; absent ⇒ read-only tabs (today's behavior). */
  editing?: FileTabsEditing;
  /** Optional controls rendered at the right end of the tab strip (e.g. a
   *  panel-position picker). Kept as a slot so this stays layout-agnostic. */
  toolbar?: React.ReactNode;
  className?: string;
}

function basename(path: string): string {
  return path.split('/').pop() || path;
}

/**
 * A VSCode-style tabbed file viewer: a horizontal strip of closable tabs (one per
 * open file) above a single {@link FileViewerPane} driven by the active tab. The
 * tree stays a separate element — it feeds `openPaths`/`activePath` through the
 * host. Purely presentational: all open-files state lives in the host.
 *
 * With `editing`: a dirty tab shows a dot in place of the close × until
 * hovered (VS Code pattern), and closing it opens a save/discard/cancel
 * confirmation instead of discarding silently.
 */
export function FileTabsViewer({
  openPaths,
  activePath,
  onActivate,
  onClose,
  read,
  activeSizeBytes,
  onOpenExternally,
  editing,
  toolbar,
  className,
}: FileTabsViewerProps) {
  // Which dirty tab has a pending close confirmation (null = no dialog).
  const [confirmClosePath, setConfirmClosePath] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  // The pane registers a flush of its pending buffer sync here, so the
  // dialog's "Save & close" acts on the latest text — including edits typed
  // inside the sub-debounce window right before the close click.
  const flushPendingEditsRef = React.useRef<(() => void) | null>(null);

  const onTabKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const delta = e.key === 'ArrowRight' ? 1 : -1;
    const next = openPaths[(index + delta + openPaths.length) % openPaths.length];
    if (next) onActivate(next);
  };

  // Close request: dirty tabs confirm; clean tabs close immediately. A dirty
  // notFound tab also confirms — its "save" resolves as save-as-new host-side.
  const requestClose = (path: string) => {
    if (editing?.states[path]?.dirty) setConfirmClosePath(path);
    else onClose(path);
  };

  const activeState = editing && activePath ? editing.states[activePath] : undefined;
  const paneEditing =
    editing && activePath && activeState
      ? {
          readOnly: editing.readOnly,
          readOnlyNotice: editing.readOnlyNotice,
          state: activeState,
          onEdit: editing.onEdit,
          onBufferSync: editing.onBufferSync,
          onSave: editing.onSave,
          onResolve: editing.onResolve,
          editMode,
          onEditModeChange: setEditMode,
        }
      : undefined;

  const confirmState = confirmClosePath ? editing?.states[confirmClosePath] : undefined;
  const showEditToggle =
    editing != null && !editing.readOnly && activePath != null && activeState != null && hasEditToggle(activePath);
  const pinnedToEdit = activeState?.dirty === true;

  return (
    <div className={cn('flex h-full min-h-0 flex-col', className)} data-testid="file-tabs-viewer">
      {/* Tab strip */}
      <div className="flex items-stretch border-b border-border bg-muted/30">
        <div role="tablist" className="flex min-w-0 flex-1 overflow-x-auto">
          {openPaths.length === 0 ? (
            <span className="px-3 py-2 text-caption text-muted-foreground">No files open</span>
          ) : (
            openPaths.map((path, i) => {
              const active = path === activePath;
              const dirty = editing?.states[path]?.dirty === true;
              return (
                <div
                  key={path}
                  role="tab"
                  aria-selected={active}
                  tabIndex={active ? 0 : -1}
                  data-testid={`file-tab-${path}`}
                  title={path}
                  onClick={() => onActivate(path)}
                  onKeyDown={(e) => onTabKeyDown(e, i)}
                  onAuxClick={(e) => { if (e.button === 1) requestClose(path); }}
                  className={cn(
                    'group flex max-w-[200px] shrink-0 cursor-pointer items-center gap-1.5 px-3 py-1.5 text-caption transition-colors',
                    active
                      ? 'bg-background text-foreground'
                      : 'text-muted-foreground hover:bg-background/50',
                  )}
                >
                  <span className="truncate">{basename(path)}</span>
                  {/* ONE always-clickable close control (clicking the dirty
                      dot closes too — VS Code behavior). The dot and the ×
                      are stacked VISUALS inside it, swapped on hover via
                      opacity; the button itself is never hover-gated, so
                      pointer tests and touch users always reach it. */}
                  <button
                    type="button"
                    aria-label={dirty ? `Close ${basename(path)} (unsaved changes)` : `Close ${basename(path)}`}
                    data-testid={`close-file-tab-${path}`}
                    onClick={(e) => { e.stopPropagation(); requestClose(path); }}
                    className="relative flex h-4 w-4 items-center justify-center rounded-sm hover:bg-accent"
                  >
                    {dirty && (
                      <span
                        data-testid={`dirty-file-tab-${path}`}
                        aria-hidden="true"
                        className="absolute inset-0 flex items-center justify-center opacity-100 transition-opacity group-hover:opacity-0"
                      >
                        <span className="h-2 w-2 rounded-full bg-foreground/70" />
                      </span>
                    )}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="11"
                      height="11"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className={cn(
                        'transition-opacity',
                        dirty
                          ? 'opacity-0 group-hover:opacity-70'
                          : active
                            ? 'opacity-70'
                            : 'opacity-0 group-hover:opacity-70',
                      )}
                    >
                      <path d="M18 6 6 18" /><path d="m6 6 12 12" />
                    </svg>
                  </button>
                </div>
              );
            })
          )}
        </div>
        {(toolbar || showEditToggle) && (
          <div className="flex shrink-0 items-center gap-1 px-2">
            {toolbar}
            {showEditToggle && (
              <ViewEditToggle
                editing={editMode || pinnedToEdit}
                onChange={setEditMode}
                disabled={pinnedToEdit}
                title={pinnedToEdit ? 'Save to preview' : undefined}
              />
            )}
          </div>
        )}
      </div>

      {/* Active file body */}
      <div className="min-h-0 flex-1 overflow-hidden">
        <FileViewerPane
          path={activePath}
          read={read}
          sizeBytes={activeSizeBytes}
          onOpenExternally={onOpenExternally}
          editing={paneEditing}
          flushPendingEditsRef={flushPendingEditsRef}
        />
      </div>

      {/* Dirty-close confirmation (never discard silently). */}
      <Dialog
        open={confirmClosePath !== null}
        onOpenChange={(open) => { if (!open) setConfirmClosePath(null); }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unsaved changes</DialogTitle>
            <DialogDescription>
              {confirmClosePath ? basename(confirmClosePath) : ''} has unsaved changes. Save them before closing?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              data-testid="cancel-close-dirty-tab"
              onClick={() => setConfirmClosePath(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              data-testid="discard-close-dirty-tab"
              onClick={() => {
                const p = confirmClosePath;
                setConfirmClosePath(null);
                if (p) editing?.onResolve(p, 'discard-close');
              }}
            >
              Discard changes
            </Button>
            <Button
              type="button"
              data-testid="save-close-dirty-tab"
              disabled={editing?.readOnly === true}
              onClick={() => {
                const p = confirmClosePath;
                setConfirmClosePath(null);
                // Flush the pane's pending sync FIRST so the save acts on the
                // latest text (the pane's flush targets the active path; a
                // non-active tab's buffer was already flushed on tab switch).
                if (p === activePath) flushPendingEditsRef.current?.();
                // Host semantics: save (as-new when the file vanished), close
                // on success; on failure the tab stays with its error banner.
                if (p) editing?.onResolve(p, 'save-close');
              }}
            >
              {confirmState?.notFound ? 'Save as new & close' : 'Save & close'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
